import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const NavCard = ({ label, description, onClick, icon, accent }) => (
  <button
    onClick={onClick}
    className="card bg-base-100 border border-slate-200 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-200 text-left w-full group"
  >
    <div className={`h-1 w-full rounded-t-2xl ${accent}`}></div>
    <div className="card-body p-5 gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent} bg-opacity-10`}>
        {icon}
      </div>
      <div>
        <div className="font-semibold text-slate-800 group-hover:text-blue-700 transition-colors">{label}</div>
        <div className="text-sm text-slate-500 mt-0.5">{description}</div>
      </div>
      <div className="flex items-center gap-1 text-xs text-slate-400 group-hover:text-blue-500 transition-colors mt-auto pt-1">
        Open
        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </div>
  </button>
);

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const isAdmin = user?.roles?.includes('ROLE_ADMIN');

  const navItems = [
    {
      label: 'Schedule',
      description: 'Manage rounds and games for all projects',
      accent: 'bg-blue-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      onClick: () => navigate('/schedule'),
    },
    {
      label: 'Players',
      description: 'Add and edit players, photos and details',
      accent: 'bg-purple-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
      onClick: () => navigate('/players'),
    },
    {
      label: 'Player Assignments',
      description: 'Assign players to teams and seasons',
      accent: 'bg-green-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      onClick: () => navigate('/player-assignments'),
    },
    {
      label: 'Widget Generator',
      description: 'Create embeddable widgets for your website',
      accent: 'bg-amber-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      onClick: () => navigate('/generator'),
    },
    {
      label: 'Settings',
      description: 'Projects, teams, seasons and reference data',
      accent: 'bg-slate-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      onClick: () => navigate('/settings'),
    },
    ...(isAdmin ? [{
      label: 'User Management',
      description: 'Manage accounts, roles and passwords',
      accent: 'bg-red-500',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      onClick: () => navigate('/users'),
    }] : []),
  ];

  return (
    <div className="space-y-6">
      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Welcome back, {user?.username || 'Admin'}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">What would you like to do today?</p>
        </div>
      </div>

      {/* Navigation grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {navItems.map((item) => (
          <NavCard key={item.label} {...item} />
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
